import { ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import { useEnrichedAuth } from './hooks/useEnrichedAuth';
import { usePreferenceLoader } from './hooks/usePreferenceLoader';
import { ProtectedRoute } from './components/ccms-components/auth/components/ProtectedRoute';
import { LoadingScreen } from './components/common/LoadingScreen';
import { NoAccessScreen } from './components/common/NoAccessScreen';

// Layouts
import { AuthLayout } from './layouts/AuthLayout';
import { OnboardingLayout } from './layouts/OnboardingLayout';
import MainLayout from './layouts/MainLayout';

// Auth pages
import LoginPage from './pages/auth/login';
import ForgotPasswordPage from './pages/auth/forgot-password';
import ResetPasswordPage from './pages/auth/reset-password';
import OnboardingPage from './pages/auth/onboarding';
import PlanningCentreCallbackPage from './pages/auth/planning-centre-callback';

// App pages
import CounsellingOverviewPage from './pages/Counselling/Overview';
import GrowOverviewPage from './pages/Grow/Overview';
import DashboardPage from './pages/Dashboard';
import ProfilePage from './pages/profile';
import SettingsPage from './pages/settings';
import PageTemplate from './pages/PageTemplate';

//Information
import CareSupportLeadersPage from './pages/Information/CareSupportLeaders';
import AddLeaderWizard from './pages/Information/CareSupportLeaders/Wizard/AddLeaderWizard';
import LeadersDetailsPage from './pages/Information/CareSupportLeaders/[LeadersDetails]';
import FacilitatorPage from './pages/Information/facilitators';
import FacilitatorDetailsPage from './pages/Information/facilitators/[FacilitorDetails]';

// Counseling
import CasePage from './pages/Counselling/Cases';
import CaseDetailsPage from './pages/Counselling/Cases/[CaseDetails]';
import MyCaseDetailsPage from './pages/Counselling/MyCases/[CaseDetails]';
import CreateCasePage from './pages/Counselling/Cases/CreateCaseWizard/CreateCasePage';
import CampusCasesPage from './pages/Counselling/CampusCases';
import PendingCasesPage from './pages/Counselling/PendingCases';
import MyCasePage from './pages/Counselling/MyCases'
import NewCasesPage from './pages/Counselling/NewCases';
import AssignedCasesPage from './pages/Counselling/AssignedCases';
import InProgressCasesPage from './pages/Counselling/InProgressCases';
import RequestsPage from './pages/Counselling/Requests';
import RequestDetailsPage from './pages/Counselling/Requests/[RequestDetails]';

// Grow
import IntakesPage from './pages/Grow/Intakes';
import IntakeDetailsPage from './pages/Grow/Intakes/[IntakeDetails]';
import RegistrationPage from './pages/Grow/Registration';
import RegistrationDetailsPage from './pages/Grow/Registration/[RegistrationDetails]';
import CreateRegistrationWizard from './pages/Grow/Registration/Form/CreateRegistrationWizard';
import CampusRegistrationsPage from './pages/Grow/CampusRegistrations';
import CampusRegistrationDetailsPage from './pages/Grow/CampusRegistrations/[RegistrationDetails]';
import EnrolmentPage from './pages/Grow/Enrolment';
import EnrolmentDetailsPage from './pages/Grow/Enrolment/[RegistrationDetails]';
import ActiveEnrolmentsPage from './pages/Grow/Enrolment/ActiveEnrolments';
import CancelledEnrolmentsPage from './pages/Grow/Enrolment/CancelledEnrolments';
import PendingEnrolmentsPage from './pages/Grow/Enrolment/PendingEnrolments';
import MyCoursesPage from './pages/Grow/AssignedCourses';
import CoursesPage from './pages/Grow/Courses';
import CourseDetailsPage from './pages/Grow/Courses/[CourseDetails]';

// Report
// import CourseDatesPage from './pages/Grow/CourseDates';
import CaseReportPage from './pages/Reports/Case';

// look ups
import NeedLookupPage from './pages/LookUp/ActsCareCounseling/Needs';
import CourseStatusPage from './pages/LookUp/ActsCareCourses/CourseStatus';
import FacilitatorStatusPage from './pages/LookUp/ActsCareCourses/FacilitatorStatus';
import  SessionStatusPage from './pages/LookUp/ActsCareCourses/SessionStatus';
import  SessionProgressStatusPage from './pages/LookUp/ActsCareCourses/SessionProgressStatus';
import LookUpPage from './pages/LookUp'
import  RegistrationStatusPage from './pages/LookUp/ActsCareCourses/RegistrationStatus';

import AdministrationSettingsPage from './pages/Administration/Settings'
import AdministrationUsersPage from './pages/Administration/Users'
import AdministrationMembersPage from './pages/Administration/Members'
import AdministrationExportPage from './pages/Administration/Export'

import ActsCareCourseIndexPage from './pages/LookUp/ActsCareCourses';
import  SessionCounselingStatusPage from './pages/LookUp/ActsCareCounseling/SessionStatus';
import  SessionTypePage from './pages/LookUp/ActsCareCounseling/SessionType';
import CaseStatusPage from './pages/LookUp/ActsCareCounseling/CaseStatus';
import LeaderTypePage from './pages/LookUp/ActsCareCounseling/LeaderType';
import ActsCareCounselingIndexPage from './pages/LookUp/ActsCareCounseling';
import LocationIndexPage from './pages/LookUp/Location';
import AreaPage from './pages/LookUp/Location/Area';
import ZonePage from './pages/LookUp/Location/Zone';

// components
import { CAMPUS_ROLES, campusSlug } from './components/common/CampusRole';



import DemoRoutes from './pages/Demo';
import CampusCaseDetailsPage from './pages/Counselling/CampusCases/[CaseDetails]';

/**
 * Flat single-app router.
 *
 * Public auth routes + onboarding, then the protected app shell (Dashboard,
 * Profile, Settings, Demo) rendered inside MainLayout. New apps add their own
 * protected routes alongside these.
 */
function App() {
  const { isAuthenticated, user, isLoading, hasAnyRole, logout } = useEnrichedAuth();
  const { hasLocalPreferences, isLoadingPreferences } = usePreferenceLoader();

  // Redirect to external site if the user is authenticated but does not have any of the required roles.
  const shouldRedirectToExternal =
    isAuthenticated &&
    user &&
    !hasAnyRole(['ActsCareFacilitator', 'ActsCareAdmin', 'ActsCareLeader', ...CAMPUS_ROLES]);

  // useEffect(() => {
  //   if (!isLoading && shouldRedirectToExternal) {
  //     window.location.href = 'https://ccms.actschurch.co.za';
  //   }
  // }, [isLoading, shouldRedirectToExternal]);

  // Show loading screen while auth is loading OR preferences are loading
  if (isLoading || (isAuthenticated && isLoadingPreferences)) {
    return <LoadingScreen />;
  }

  // Force onboarding if the user hasn't completed it, or has completed it but
  // has no local preferences (e.g. fresh device login).
  const requiresOnboarding =
    isAuthenticated &&
    user &&
    (!user.onboardingCompleted || (user.onboardingCompleted && !hasLocalPreferences));

  // Wraps a protected page in the standard guards + main layout.
  const protectedPage = (element: ReactNode, layout: boolean = true, roles?: string[], rights?: string[]) => (
    <ProtectedRoute
      requireAuth
      requiredRoles={roles}
      requiredPermissions={rights}
    >
      {requiresOnboarding ? (
        <Navigate to="/auth/onboarding" replace />
      ) : layout ? (
        <MainLayout>{element}</MainLayout>
      ) : (
        element
      )}
    </ProtectedRoute>
  );

  const getAuthedRedirectPath = () => {
    if (requiresOnboarding) {
      return '/auth/onboarding';
    }

    if (hasAnyRole(['ActsCareFacilitator', 'ActsCareLeader'])) {
      return '/Dashboard';
    }

    if (hasAnyRole(['ActsCareAdmin'])) {
      return '/Counselling/Overview';
    }

    const campusRole = user?.roles?.find((role) => CAMPUS_ROLES.includes(role));
    if (campusRole) {
      return `/Counselling/CampusCases/${campusSlug(campusRole)}`;
    }

    // Signed-out visitors land here (e.g. hitting `/` directly), and for them `/auth/login` renders the
    // login page rather than redirecting, so there is no loop.
    //
    // ⚠️ An **authenticated** user with no Acts Care role must never reach this line. It is what used to
    // return them to `/auth/login`, the route that renders this very redirect, looping forever.
    // `shouldRedirectToExternal` now intercepts that case below.
    return '/auth/login';
  };

  /**
   * What an already-authenticated user sees on an auth page.
   *
   * ⚠️ Every landing page is role-gated, so a user holding **none** of the three Acts Care roles has
   * nowhere to be sent. This used to fall back to `'/auth/login'`, which is the very route rendering
   * this element, so it redirected to itself forever: blank screen, no thrown error, and endless
   * re-render churn. A redirect can never resolve "there is nowhere to go", so render instead.
   */
  const authedRedirect = shouldRedirectToExternal ? (
    <NoAccessScreen onSignOut={() => { void logout(); }} />
  ) : (
    <Navigate to={getAuthedRedirectPath()} replace />
  );

  return (
    <Routes>
      {/* ---- Public auth routes ---- */}
      <Route
        path="/auth/login"
        element={isAuthenticated ? authedRedirect : <AuthLayout><LoginPage /></AuthLayout>}
      />
      <Route
        path="/auth/forgot-password"
        element={
          isAuthenticated ? (
            authedRedirect
          ) : (
            <AuthLayout showBackButton backTo="/auth/login">
              <ForgotPasswordPage />
            </AuthLayout>
          )
        }
      />
      <Route
        path="/auth/reset-password"
        element={
          isAuthenticated ? authedRedirect : <AuthLayout><ResetPasswordPage /></AuthLayout>
        }
      />

      {/* Planning Centre OAuth callback — anonymous landing target. Not
          redirected away when authenticated (re-link flow). */}
      <Route
        path="/auth/oauth/planning-centre/callback"
        element={
          <AuthLayout>
            <PlanningCentreCallbackPage />
          </AuthLayout>
        }
      />

      {/* ---- Onboarding (auth required, before full app access) ---- */}
      <Route
        path="/auth/onboarding"
        element={
          <ProtectedRoute requireAuth fallback={<Navigate to="/auth/login" replace />}>
            
            <OnboardingLayout>
              <OnboardingPage />
            </OnboardingLayout>
          </ProtectedRoute>
        }
      />

      {/* ---- Protected app shell ---- */}
      {/* `/` is a pure role-based redirect: Admin to /Counselling/Overview,
          Leader/Facilitator to /Dashboard. */}
      <Route path="/" element={authedRedirect} />
      {/* Legacy path. The single combined Overview was split per section. */}
      <Route path="/Overview" element={<Navigate to="/Counselling/Overview" replace />} />
      <Route path="/Dashboard" element={protectedPage(<DashboardPage />, true, ['ActsCareFacilitator', 'ActsCareLeader'])} />
      {/* Legacy path — the leader/facilitator landing page was called Control Center. */}
      <Route path="/ControlCenter" element={<Navigate to="/Dashboard" replace />} />
      <Route path="/profile" element={protectedPage(<ProfilePage />, true, ['ActsCareFacilitator', 'ActsCareAdmin', 'ActsCareLeader', ...CAMPUS_ROLES])} />
      <Route path="/settings" element={protectedPage(<SettingsPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin', 'ActsCareLeader', ...CAMPUS_ROLES])} />
      <Route path="/template" element={protectedPage(<PageTemplate />, true, ['ActsCareFacilitator', 'ActsCareAdmin','ActsCareLeader'])} />

      <Route path="/Information/CareSupportLeaders" element={protectedPage(<CareSupportLeadersPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Information/CareSupportLeaders/Leaders/:id" element={protectedPage(<LeadersDetailsPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Information/CareSupportLeaders/Wizard/AddLeaderWizard" element={protectedPage(<AddLeaderWizard />, true, ['ActsCareAdmin'])} />
      <Route path="/Information/facilitators" element={protectedPage(<FacilitatorPage />, true, ['ActsCareAdmin'])}/>
      <Route path="/Information/facilitators/:id" element={protectedPage(<FacilitatorDetailsPage />, true, ['ActsCareAdmin'])} />

      <Route path="/Counselling/Overview" element={protectedPage(<CounsellingOverviewPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Counselling/Requests" element={protectedPage(<RequestsPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Counselling/Requests/:id" element={protectedPage(<RequestDetailsPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Counselling/Cases" element={protectedPage(<CasePage />, true, ['ActsCareAdmin'])} />
      <Route path="/Counselling/Cases/:id" element={protectedPage(<CaseDetailsPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Counselling/Cases/CreateCaseWizard" element={protectedPage(<CreateCasePage />, true, ['ActsCareAdmin'])}/>
      <Route path="/Counselling/MyCases/:id" element={protectedPage(<MyCaseDetailsPage />, true, ['ActsCareLeader'])} />
      <Route path="/Counselling/CampusCases" element={protectedPage(<CampusCasesPage />, true, CAMPUS_ROLES)} />
      <Route path="/Counselling/CampusCases/:campus" element={protectedPage(<CampusCasesPage />, true, CAMPUS_ROLES)} />
      <Route path="/Counselling/CampusCases/:campus/:id" element={protectedPage(<CampusCaseDetailsPage />, true, CAMPUS_ROLES)} />
      <Route path="/Counselling/PendingCases" element={protectedPage(<PendingCasesPage />, true, ['ActsCareLeader'])} />
      <Route path="/Counselling/MyCases" element={protectedPage(<MyCasePage />, true, ['ActsCareLeader'])} />
      <Route path="/Counselling/NewCases" element={protectedPage(<NewCasesPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Counselling/AssignedCases" element={protectedPage(<AssignedCasesPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Counselling/InProgressCases" element={protectedPage(<InProgressCasesPage />, true, ['ActsCareAdmin'])} />


      <Route path="/Grow/Overview" element={protectedPage(<GrowOverviewPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Grow/Intakes" element={protectedPage(<IntakesPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Grow/Intakes/:id" element={protectedPage(<IntakeDetailsPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Grow/Registration" element={protectedPage(<RegistrationPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Registration/RegistrationDetails/:id" element={protectedPage(<RegistrationDetailsPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Registration/Form/CreateRegistrationWizard" element={protectedPage(<CreateRegistrationWizard />, true, ['ActsCareAdmin'])} />
      <Route path="/Grow/CampusRegistrations" element={protectedPage(<CampusRegistrationsPage />, true, CAMPUS_ROLES)} />
      <Route path="/Grow/CampusRegistrations/:campus" element={protectedPage(<CampusRegistrationsPage />, true, CAMPUS_ROLES)} />
      <Route path="/Grow/CampusRegistrations/:campus/:id" element={protectedPage(<CampusRegistrationDetailsPage />, true, CAMPUS_ROLES)} />
      <Route path="/Grow/Enrolment/PendingEnrolments" element={protectedPage(<PendingEnrolmentsPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Enrolment/ActiveEnrolments" element={protectedPage(<ActiveEnrolmentsPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Enrolment/CancelledEnrolments" element={protectedPage(<CancelledEnrolmentsPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/AssignedCourses" element={protectedPage(<MyCoursesPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Enrolment/RegistrationDetails/:id" element={protectedPage(<EnrolmentDetailsPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Enrolment" element={protectedPage(<EnrolmentPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/AssignedCourses/MyCourses" element={protectedPage(<MyCoursesPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Courses" element={protectedPage(<CoursesPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      <Route path="/Grow/Courses/CourseDetails/:id" element={protectedPage(<CourseDetailsPage />, true, ['ActsCareFacilitator', 'ActsCareAdmin'])} />
      {/* <Route path="/Grow/CourseDates" element={protectedPage(<CourseDatesPage />)} /> */}

      <Route path="/Reports/Case" element={protectedPage(<CaseReportPage />, true, ['ActsCareAdmin'])} />

      <Route path="/LookUp/ActsCareCounseling/Needs" element={protectedPage(<NeedLookupPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCourses/CourseStatus" element={protectedPage(<CourseStatusPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCourses/FacilitatorStatus" element={protectedPage(<FacilitatorStatusPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCourses/RegistrationStatus" element={protectedPage(<RegistrationStatusPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCourses/RegistrationStatus" element={protectedPage(<RegistrationStatusPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCourses/SessionStatus" element={protectedPage(<SessionStatusPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCourses" element={protectedPage(<ActsCareCourseIndexPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCourses/SessionProgressStatus" element={protectedPage(<SessionProgressStatusPage />, true, ['ActsCareAdmin'])} />
      
      <Route path="/LookUp/ActsCareCounseling/SessionStatus" element={protectedPage(<SessionCounselingStatusPage />, true, ['ActsCareLeader', 'ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCounseling/SessionType" element={protectedPage(<SessionTypePage />, true, ['ActsCareLeader', 'ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCounseling/CaseStatus" element={protectedPage(<CaseStatusPage />, true, ['ActsCareLeader', 'ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCounseling/LeaderType" element={protectedPage(<LeaderTypePage />, true, ['ActsCareLeader', 'ActsCareAdmin'])} />
      <Route path="/LookUp/ActsCareCounseling" element={protectedPage(<ActsCareCounselingIndexPage />, true, ['ActsCareLeader', 'ActsCareAdmin'])} />
      <Route path="/LookUp/Location" element={protectedPage(<LocationIndexPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/Location/Area" element={protectedPage(<AreaPage />, true, ['ActsCareAdmin'])} />
      <Route path="/LookUp/Location/Zone" element={protectedPage(<ZonePage />, true, ['ActsCareAdmin'])} />


      <Route path="/LookUp" element={protectedPage(<LookUpPage />, true, ['ActsCareAdmin'])} />

      {/* ---- Administration (admin only) ---- */}
      <Route path="/Administration/Settings" element={protectedPage(<AdministrationSettingsPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Administration/Users" element={protectedPage(<AdministrationUsersPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Administration/Members" element={protectedPage(<AdministrationMembersPage />, true, ['ActsCareAdmin'])} />
      <Route path="/Administration/Export" element={protectedPage(<AdministrationExportPage />, true, ['ActsCareAdmin'])} />





      {/* Demo brings its own layout (DemoLayout) + sub-routing */}
      <Route path="/demo/*" element={protectedPage(<DemoRoutes />, false)} />

      {/* ---- Catch-all ---- */}
      <Route
        path="*"
        element={
          <Navigate
            to={isAuthenticated ? (requiresOnboarding ? '/auth/onboarding' : '/') : '/auth/login'}
            replace
          />
        }
      />
    </Routes>
  );
}

export default App;
